import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";
import { getOrderDetailsAdmin, updateOrderStatus } from "../adminApi";
import { getImageUrl } from "../../utils/imageUrl";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOrderDetailsAdmin(orderId);
        setOrder(data);
      } catch (err) {
        setError(err.message || "Failed to load order details");
        console.error("Error loading order:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  const handleStatusChange = async (newStatus) => {
    try {
      setIsUpdatingStatus(true);
      setStatusUpdateError(null);
      await updateOrderStatus(orderId, { status: newStatus });
      // Update local state
      setOrder((prev) => ({ ...prev, status: newStatus }));
    } catch (err) {
      setStatusUpdateError(err.message || "Failed to update order status");
      console.error("Error updating status:", err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      processing: "bg-purple-100 text-purple-800",
      shipped: "bg-cyan-100 text-cyan-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      refunded: "bg-gray-100 text-gray-800",
    };
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center min-h-96">
          <FaSpinner className="w-12 h-12 text-blue-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button
          onClick={() => navigate("/dashboard-admin/orders")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <FaArrowLeft /> Back to Orders
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button
          onClick={() => navigate("/dashboard-admin/orders")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <FaArrowLeft /> Back to Orders
        </button>
        <div className="text-center py-12 text-gray-500">Order not found</div>
      </div>
    );
  }

  const customer = order.User || {};
  const address = order.ShippingAddress || {};
  const items = order.OrderItems || [];

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Calculate totals
  const subtotal = items.reduce(
    (sum, item) => sum + toNumber(item.subtotal),
    0,
  );
  const tax = toNumber(order.tax);
  const shipping = toNumber(order.shipping_cost);
  const total =
    order.total_price !== undefined && order.total_price !== null
      ? toNumber(order.total_price)
      : subtotal + tax + shipping;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate("/dashboard-admin/orders")}
          className="text-gray-600 hover:text-gray-900 transition-colors"
          title="Back to Orders"
        >
          <FaArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-extrabold">Order #{order.order_id}</h1>
          <p className="text-sm text-gray-500">
            {new Date(order.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>
      </div>

      {statusUpdateError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {statusUpdateError}
        </div>
      )}

      {/* Order Status Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold mb-2">Order Status</h2>
            <p
              className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}
            >
              {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <label className="text-sm text-gray-600">Update Status:</label>
            <select
              value={order.status || ""}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={isUpdatingStatus}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Customer Information</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Name</p>
            <p className="font-medium">{customer.name || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Email</p>
            <p className="font-medium">{customer.email || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Phone</p>
            <p className="font-medium">{customer.phone_number || "N/A"}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Customer ID</p>
            <p className="font-medium text-xs">{customer.user_id || "N/A"}</p>
          </div>
        </div>
      </div>

      {/* Shipping Address */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Shipping Address</h2>
        {address && Object.keys(address).length > 0 ? (
          <div className="text-gray-700">
            <p className="font-medium">{address.full_name}</p>
            <p>{address.street_address}</p>
            <p>
              {address.city}, {address.state} {address.postal_code}
            </p>
            <p>{address.country}</p>
            <p className="mt-2 text-sm">{address.phone_number}</p>
          </div>
        ) : (
          <p className="text-gray-500">No address information available</p>
        )}
      </div>

      {/* Order Items */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Order Items</h2>
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b-2 border-gray-300">
                <tr>
                  <th className="text-left py-2 px-2 font-semibold">Product</th>
                  <th className="text-left py-2 px-2 font-semibold">SKU</th>
                  <th className="text-center py-2 px-2 font-semibold">Qty</th>
                  <th className="text-right py-2 px-2 font-semibold">
                    Unit Price
                  </th>
                  <th className="text-right py-2 px-2 font-semibold">
                    Subtotal
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.orderItem_id}
                    className="border-b border-gray-200"
                  >
                    <td className="py-3 px-2">
                      <a
                        href={`/product/${item.Product?.product_id}`}
                        className="flex items-center gap-3 hover:bg-gray-50 rounded-md p-1 -m-1 transition-colors"
                        title="Open product page"
                      >
                        <img
                          src={
                            item.Product?.images?.[0]?.image_url
                              ? getImageUrl(item.Product.images[0].image_url)
                              : "https://placehold.co/64x64?text=No+Image"
                          }
                          alt={item.Product?.name_en || "Product"}
                          className="w-12 h-12 object-cover rounded-md border border-gray-200"
                        />
                        <span className="font-medium text-blue-700 hover:underline">
                          {item.Product?.name_en ||
                            `Product #${item.product_id}`}
                        </span>
                      </a>
                    </td>
                    <td className="py-3 px-2 text-gray-700">
                      {item.Product?.sku || item.Product?.model_code || "N/A"}
                    </td>
                    <td className="text-center py-3 px-2">{item.quantity}</td>
                    <td className="text-right py-3 px-2">
                      ${toNumber(item.price).toFixed(2)}
                    </td>
                    <td className="text-right py-3 px-2 font-medium">
                      ${toNumber(item.subtotal).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">No items in this order</p>
        )}
      </div>

      {/* Order Summary */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-700">Subtotal:</span>
            <span className="font-medium">${subtotal.toFixed(2)}</span>
          </div>
          {shipping > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-700">Shipping:</span>
              <span className="font-medium">${shipping.toFixed(2)}</span>
            </div>
          )}
          {tax > 0 && (
            <div className="flex justify-between">
              <span className="text-gray-700">Tax:</span>
              <span className="font-medium">${tax.toFixed(2)}</span>
            </div>
          )}
          <div className="border-t border-gray-300 pt-2 mt-2 flex justify-between text-lg font-bold">
            <span>Total:</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Order Metadata */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">
          Order Details
        </h3>
        <div className="text-xs text-gray-600 space-y-1">
          <p>
            <span className="font-medium">Order ID:</span> {order.order_id}
          </p>
          <p>
            <span className="font-medium">Payment Status:</span>{" "}
            {order.payment_status || "N/A"}
          </p>
          <p>
            <span className="font-medium">Created:</span>{" "}
            {new Date(order.createdAt).toLocaleString()}
          </p>
          {order.updatedAt && (
            <p>
              <span className="font-medium">Updated:</span>{" "}
              {new Date(order.updatedAt).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
